import React, { useState } from "react";
import "./CockpitVariants.scss";

/**
 * Different mood variations of the gaming cockpit
 * Each creates a different emotional experience
 */
const CockpitVariants: React.FC = () => {
    const [activeVariant, setActiveVariant] = useState<"tactical" | "neon" | "minimal" | "danger">("tactical");

    return (
        <div className={`cockpit-variants cockpit-variants--${activeVariant}`}>
            {/* Variant Selector */}
            <div className="variant-selector">
                <button onClick={() => setActiveVariant("tactical")} className={activeVariant === "tactical" ? "active" : ""}>
                    TACTICAL OPS
                </button>
                <button onClick={() => setActiveVariant("neon")} className={activeVariant === "neon" ? "active" : ""}>
                    NEON RUSH
                </button>
                <button onClick={() => setActiveVariant("minimal")} className={activeVariant === "minimal" ? "active" : ""}>
                    STEALTH MODE
                </button>
                <button onClick={() => setActiveVariant("danger")} className={activeVariant === "danger" ? "active" : ""}>
                    DANGER ZONE
                </button>
            </div>

            {/* VARIANT 1: TACTICAL OPS - Military precision, green/amber HUD */}
            {activeVariant === "tactical" &&
        <div className="tactical-ops">
            <div className="tactical-grid">
                <div className="crosshair" />
                <div className="range-finder">
                    <span>RANGE: 2.4KM</span>
                    <span>BEARING: 045°</span>
                </div>
            </div>

            <div className="mission-briefing">
                <div className="classification">CLASSIFIED // EYES ONLY</div>
                <h1 className="operation-name">OPERATION: CRIMSON SPEAR</h1>
                <div className="mission-timer">
                    <span className="timer-label">MISSION WINDOW</span>
                    <span className="timer-value">T-MINUS 04:23:16</span>
                </div>

                <div className="tactical-data">
                    <div className="data-block">
                        <span className="data-label">THREAT LEVEL</span>
                        <div className="threat-meter">
                            <div className="threat-bar" style={{width: "75%"}} />
                        </div>
                    </div>
                    <div className="data-block">
                        <span className="data-label">SQUAD READINESS</span>
                        <div className="readiness-indicators">
                            <span className="ready">●</span>
                            <span className="ready">●</span>
                            <span className="standby">●</span>
                            <span className="offline">●</span>
                        </div>
                    </div>
                </div>

                <button className="deploy-button">
                    COMMENCE OPERATION
                    <span className="button-glow" />
                </button>
            </div>

            <div className="tactical-feed">
                <div className="feed-entry">[ 23:42:01 ] ALPHA TEAM IN POSITION</div>
                <div className="feed-entry">[ 23:41:45 ] OVERWATCH ESTABLISHED</div>
                <div className="feed-entry">[ 23:41:32 ] COMMS CHECK COMPLETE</div>
            </div>
        </div>
            }

            {/* VARIANT 2: NEON RUSH - Cyberpunk vibes, pink/blue gradients */}
            {activeVariant === "neon" &&
        <div className="neon-rush">
            <div className="neon-rain" />
            <div className="glitch-overlay" />

            <div className="cyber-frame">
                <div className="frame-corner frame-corner--tl" />
                <div className="frame-corner frame-corner--tr" />
                <div className="frame-corner frame-corner--bl" />
                <div className="frame-corner frame-corner--br" />
            </div>

            <div className="neon-content">
                <div className="cyber-badge">STREET SAMURAI</div>
                <h1 className="neon-title">
                    <span className="glitch" data-text="NEON DISTRICT">NEON DISTRICT</span>
                    <span className="subtitle">CHAOS PROTOCOL ACTIVE</span>
                </h1>

                <div className="power-levels">
                    <div className="power-bar power-bar--health">
                        <span className="bar-label">VITALITY</span>
                        <div className="bar-fill" />
                        <span className="bar-value">87%</span>
                    </div>
                    <div className="power-bar power-bar--shield">
                        <span className="bar-label">SHIELDS</span>
                        <div className="bar-fill" />
                        <span className="bar-value">42%</span>
                    </div>
                    <div className="power-bar power-bar--boost">
                        <span className="bar-label">BOOST</span>
                        <div className="bar-fill" />
                        <span className="bar-value">READY</span>
                    </div>
                </div>

                <div className="neon-actions">
                    <button className="neon-button neon-button--primary">
                        <span className="button-text">JACK IN</span>
                        <div className="button-border" />
                    </button>
                    <button className="neon-button neon-button--secondary">
                        <span className="button-text">SCAN AREA</span>
                        <div className="button-border" />
                    </button>
                </div>
            </div>

            <div className="data-stream">
                <div className="stream-line">[SYNC] Neural link established...</div>
                <div className="stream-line">[DATA] Downloading sector intel...</div>
                <div className="stream-line">[WARN] Hostile ICE detected</div>
            </div>
        </div>
            }

            {/* VARIANT 3: STEALTH MODE - Minimal, dark, spy thriller */}
            {activeVariant === "minimal" &&
        <div className="stealth-mode">
            <div className="radar-sweep">
                <div className="sweep-line" />
                <div className="radar-dot" style={{top: "30%", left: "60%"}} />
                <div className="radar-dot" style={{top: "70%", left: "20%"}} />
            </div>

            <div className="stealth-intel">
                <div className="codename">GHOST PROTOCOL</div>
                <h1 className="operation">INFILTRATION MODE</h1>
                <div className="status-line">
                    <span className="status-indicator">●</span>
                    <span className="status-text">UNDETECTED</span>
                </div>

                <div className="intel-grid">
                    <div className="intel-item">
                        <span className="intel-label">VISIBILITY</span>
                        <span className="intel-value">12M</span>
                    </div>
                    <div className="intel-item">
                        <span className="intel-label">NOISE</span>
                        <span className="intel-value">2DB</span>
                    </div>
                    <div className="intel-item">
                        <span className="intel-label">TIME</span>
                        <span className="intel-value">02:14</span>
                    </div>
                </div>

                <button className="stealth-button">
                    PROCEED WITH CAUTION
                    <span className="button-underline" />
                </button>
            </div>

            <div className="detection-meter">
                <span className="meter-label">DETECTION RISK</span>
                <div className="meter-track">
                    <div className="meter-fill" style={{width: "20%"}} />
                </div>
            </div>
        </div>
            }

            {/* VARIANT 4: DANGER ZONE - Red alerts, warning systems */}
            {activeVariant === "danger" &&
        <div className="danger-zone">
            <div className="alert-stripes" />
            <div className="warning-flash" />

            <div className="alert-header">
                <div className="alert-icon">⚠️</div>
                <div className="alert-text">CRITICAL ALERT</div>
                <div className="alert-icon">⚠️</div>
            </div>

            <div className="danger-content">
                <h1 className="danger-title">
                    BREACH DETECTED
                    <span className="blink">_</span>
                </h1>
                <div className="threat-assessment">
                    <div className="threat-level-indicator">
                        <span className="level-text">THREAT LEVEL</span>
                        <span className="level-value">EXTREME</span>
                    </div>
                    <div className="countdown">
                        <span className="countdown-label">CONTAINMENT FAILURE IN</span>
                        <span className="countdown-timer">00:47</span>
                    </div>
                </div>

                <div className="system-status">
                    <div className="status-item status-item--critical">
                        <span className="status-name">SHIELDS</span>
                        <span className="status-value">OFFLINE</span>
                    </div>
                    <div className="status-item status-item--warning">
                        <span className="status-name">HULL</span>
                        <span className="status-value">32%</span>
                    </div>
                    <div className="status-item status-item--critical">
                        <span className="status-name">WEAPONS</span>
                        <span className="status-value">OVERHEAT</span>
                    </div>
                </div>

                <div className="emergency-actions">
                    <button className="emergency-button emergency-button--evac">
                        EMERGENCY EVAC
                        <span className="button-pulse" />
                    </button>
                    <button className="emergency-button emergency-button--override">
                        MANUAL OVERRIDE
                        <span className="button-pulse" />
                    </button>
                </div>
            </div>

            <div className="damage-report">
                <div className="report-entry report-entry--critical">MAIN REACTOR: CRITICAL</div>
                <div className="report-entry report-entry--warning">LIFE SUPPORT: 67%</div>
                <div className="report-entry report-entry--critical">BREACH: SECTORS 7-12</div>
            </div>
        </div>
            }

            {/* Floating Elements that work across all variants */}
            <div className="universal-elements">
                {/* Power Core Animation */}
                <div className="power-core">
                    <div className="core-ring core-ring--outer" />
                    <div className="core-ring core-ring--middle" />
                    <div className="core-ring core-ring--inner" />
                    <div className="core-center" />
                </div>

                {/* Particle System */}
                <div className="particle-field">
                    {[...Array(20)].map((_, i) =>
                        <div key={i} className={`particle particle--${i % 3}`} />)}
                </div>
            </div>
        </div>
    );
};

export default CockpitVariants;